// FILE: /src/Components/PublishPage.js (FULLY UPDATED)

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Dropdown, Button, message, Alert } from "antd";
import { postApi, updateScheduledPost } from "../services/publish";
import { fetchGroupDetails } from "../store/Slices/GroupSlice";
import ShowProfiles from "./ShowProfiles";
import PostUploader from "./PostUploader/index"; 
import FacebookModal from "./Social Desings/FacebookModal";
import InstagarmModal from "./Social Desings/InstagarmModal";
import Pinerest from "./Social Desings/Pinerest";
import LinkedInModal from "./Social Desings/LinkedInModal";
import YoutubeModal from "./Social Desings/YoutubeModal";
import TikTokModal from "./Social Desings/TikTokModal";
import moment from "moment";
import ValidationTab from "./PostUploader/ValidationTab";
import { renderPlatformIcon, renderProfileName } from './ShowProfiles';
import UpgradeModal from "./BuySubscription/UpgradeModal";
import { getOnboardingForGroup } from "../services/group"; 
import CaptionGenerator from "./PostUploader/CaptionGenerator";
import AiImageGenerator from "./PostUploader/imagegenerator";
import SocialPostGeneratorModal from "./PostUploader/SocialPostGenerator";

export default function PublishPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const { PublishData, isEditing } = location.state || {};
    const { profile: availableProfiles, selectedGroup, groups } = useSelector((state) => state.groups);

    const [brandTimezone, setBrandTimezone] = useState(null);

    const [messages, setMessage] = useState("");
    const [photoPreview, setPhotoPreview] = useState([]);
    const [selectedProfiles, setSelectedProfiles] = useState([]);
    const [pinterestOptions, setPinterestOptions] = useState({});
    const [instagramOptions, setInstagramOptions] = useState({ firstComment: "" });
    const [linkedinOptions, setLinkedinOptions] = useState({ articleUrl: "", documentTitle: "", articleTitle: "" });
    const [youtubeOptions, setYoutubeOptions] = useState({});
    const [tiktokOptions, setTiktokOptions] = useState({
        privacy_level: undefined,
        comment_disabled: true,
        duet_disabled: true,
        stitch_disabled: true,
        brand_content_toggle: false,
    });
    
    const [scheduleType, setScheduleType] = useState('schedule');
    const [scheduleDates, setScheduleDates] = useState(() => [{ date: moment(), time: moment().add(10, 'minutes') }]);
    
    const [apiWait, setApiWait] = useState(false);
    const [showProfileList, setShowProfileList] = useState(false);
    const [postLogicErrors, setPostLogicErrors] = useState([]);
    const [fileValidationErrors, setFileValidationErrors] = useState([]);
    
    const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
    const [upgradeModalMessage, setUpgradeModalMessage] = useState('');
    const [upgradeModalFeature, setUpgradeModalFeature] = useState('');
    
    const [isCaptionModalOpen, setIsCaptionModalOpen] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isSocialPostModalOpen, setIsSocialPostModalOpen] = useState(false);
    
    const showUpgradeModal = ({ message, featureName }) => {
        setUpgradeModalMessage(message);
        setUpgradeModalFeature(featureName);
        setUpgradeModalVisible(true);
    };
    
    const hideUpgradeModal = () => {
        setUpgradeModalVisible(false);
        setUpgradeModalMessage('');
        setUpgradeModalFeature('');
    };

    useEffect(() => {
        const fetchBrandTimezone = async () => {
            if (selectedGroup) {
                const groupId = groups.find(g => g.name === selectedGroup)?._id;
                if (groupId) {
                    try {
                        const onboardingData = await getOnboardingForGroup(groupId);
                        setBrandTimezone(onboardingData?.time_zone || null);
                    } catch (error) {
                        console.warn("Could not fetch brand timezone.", error);
                        setBrandTimezone(null);
                    }
                }
            }
        };
        fetchBrandTimezone();
    }, [selectedGroup, groups]);
    
    const allValidationErrors = useMemo(() => [
        ...postLogicErrors,
        ...fileValidationErrors
    ], [postLogicErrors, fileValidationErrors]);

    const hasContent = useMemo(() => photoPreview.length > 0 || messages.trim().length > 0, [photoPreview, messages]);

    const resetForm = useCallback((duplicate = false) => {
        if (duplicate) {
            setScheduleType('schedule');
            setScheduleDates([{ date: moment(), time: moment().add(10, 'minutes') }]);
            setPostLogicErrors([]);
            setFileValidationErrors([]);
        } else {
            setMessage("");
            setPhotoPreview([]);
            setSelectedProfiles([]);
            setPinterestOptions({});
            setInstagramOptions({ firstComment: "" });
            setLinkedinOptions({ articleUrl: "", documentTitle: "", articleTitle: "" });
            setYoutubeOptions({});
            setTiktokOptions({ privacy_level: undefined, comment_disabled: true, duet_disabled: true, stitch_disabled: true, brand_content_toggle: false });
            setScheduleType('schedule');
            setScheduleDates([{ date: moment(), time: moment().add(10, 'minutes') }]);
            setPostLogicErrors([]);
            setFileValidationErrors([]);
        }
    }, []);

    useEffect(() => {
        if (selectedGroup) {
            resetForm(false);
        }
    }, [selectedGroup, resetForm]);

    useEffect(() => {
        if (!isEditing && availableProfiles && availableProfiles.length > 0 && selectedProfiles.length === 0) {
            setSelectedProfiles([availableProfiles[0]]);
        }
    }, [availableProfiles, isEditing, selectedProfiles.length]); 

    useEffect(() => {
        if (isEditing && PublishData && availableProfiles) {
            setMessage(PublishData.message || "");
            if (PublishData.media_url && PublishData.media_url.length > 0) {
                const formattedMedia = PublishData.media_url.map(url => ({
                    id: `existing_${url}`,
                    localUrl: url,
                    permanentUrl: url,
                    type: /\.(mp4|mov|webm|pdf|docx|pptx)$/i.test(url) ? 'video' : 'image',
                    file: null,
                    status: 'completed'
                }));
                setPhotoPreview(formattedMedia);
            }
            setInstagramOptions({ firstComment: PublishData.first_comment || "" });
            setTiktokOptions(PublishData.platformOptions?.tiktokOptions || { privacy_level: undefined, comment_disabled: true, duet_disabled: true, stitch_disabled: true, brand_content_toggle: false });

            if (PublishData.platformOptions && PublishData.platformOptions.PINTEREST) {
                setPinterestOptions(PublishData.platformOptions.PINTEREST);
            } else if (PublishData.board_id) { 
                const pinterestProfile = availableProfiles.find(p => p.platform === 'PINTEREST');
                if (pinterestProfile) {
                    setPinterestOptions({
                        [pinterestProfile.platform_id]: {
                            title: PublishData.title || "",
                            link: PublishData.description || "",
                            board: { id: PublishData.board_id, name: "Loading..." },
                            thumbnailUrl: PublishData.pinterest_thumbnail_url || null
                        }
                    });
                }
            }

            const lnOptions = PublishData.platformOptions?.linkedinOptions || {};
            setLinkedinOptions({
                articleUrl: lnOptions.articleUrl || "",
                documentTitle: lnOptions.documentTitle || "",
                articleTitle: lnOptions.articleTitle || ""
            });
            
            if (PublishData.platformOptions && typeof PublishData.platformOptions.YOUTUBE === 'object') {
                setYoutubeOptions(PublishData.platformOptions.YOUTUBE);
            } else {
                setYoutubeOptions({});
            }

            const hasDate = PublishData.scheduledTime && moment(PublishData.scheduledTime).isValid();
            setScheduleType(PublishData.status === 'draft' && !hasDate ? 'schedule' : 'schedule');
            setScheduleDates(hasDate ? [{ date: moment(PublishData.scheduledTime), time: moment(PublishData.scheduledTime) }] : [{ date: moment(), time: moment().add(10, 'minutes') }]);
            
            const profilesToSelect = availableProfiles.filter(p => {
                const profileId = p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id;
                return (PublishData.selectedProfiles || []).includes(profileId);
            });
            setSelectedProfiles(profilesToSelect);

        } else if (!isEditing) {
            resetForm(false);
        }
    }, [isEditing, PublishData, availableProfiles, resetForm]);
    
    const handleFileUploadError = useCallback((errorMsg) => {
        message.error(errorMsg, 4);
        setFileValidationErrors(prev => [...prev, errorMsg]);
    }, []);

    const handlePostValidationChange = useCallback((errors) => {
        setPostLogicErrors(errors);
    }, []);

    useEffect(() => {
        if (selectedGroup) {
            dispatch(fetchGroupDetails(selectedGroup));
        }
    }, [selectedGroup, dispatch]);

    const handleProfileSelectionChange = useCallback((profile, isChecked) => {
        setSelectedProfiles(prev => {
            const key = profile.platform === 'FACEBOOK' ? profile.platform_pageId : profile.platform_id;
            return isChecked
                ? (prev.some(p => (p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id) === key) ? prev : [...prev, profile])
                : prev.filter(p => (p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id) !== key);
        });
    }, []);

    const isProfileSelected = useCallback((profile) => {
        if (!profile) return false;
        const key = profile.platform === 'FACEBOOK' ? profile.platform_pageId : profile.platform_id;
        return selectedProfiles.some(p => (p.platform === 'FACEBOOK' ? p.platform_pageId : p.platform_id) === key);
    }, [selectedProfiles]);

    const buildPayload = () => {
        const groupId = groups.find(g => g.name === selectedGroup)?._id;

        if (photoPreview.some(item => item.status === 'uploading')) {
            message.error("Please wait for all media to finish uploading before proceeding.");
            return null;
        }

        const finalMediaItems = photoPreview.map(item => ({
            url: item.permanentUrl,
            type: item.type
        }));

        const determineMediaType = () => {
            if (!finalMediaItems || finalMediaItems.length === 0) return 'text';
            if (finalMediaItems.some(item => item.type === 'video')) return 'video';
            if (finalMediaItems.some(item => item.type === 'document')) return 'document';
            return 'image';
        };

        const isLinkedInSelected = selectedProfiles.some(p => p.platform === 'LINKEDIN');
        const isPinterestSelected = selectedProfiles.some(p => p.platform === 'PINTEREST');
        const isTikTokSelected = selectedProfiles.some(p => p.platform === 'TIKTOK');
        const youtubeProfiles = selectedProfiles.filter(p => p.platform === 'YOUTUBE');

        const platformOptions = {};

        if (isLinkedInSelected) {
            platformOptions.linkedinOptions = linkedinOptions;
        }

        if (isPinterestSelected) {
            platformOptions.PINTEREST = pinterestOptions;
        }
        
        if (youtubeProfiles.length > 0) {
            platformOptions.YOUTUBE = youtubeOptions;
        }

        if (isTikTokSelected) {
            platformOptions.tiktokOptions = tiktokOptions;
        }
        
        return {
            groupId,
            groupName: selectedGroup,
            media_items: finalMediaItems,
            media_type: determineMediaType(),
            selectedProfiles: selectedProfiles.map(p => p.platform === "FACEBOOK" ? p.platform_pageId : p.platform_id),
            platforms: [...new Set(selectedProfiles.map(p => p.platform))],
            message: messages,
            first_comment: instagramOptions.firstComment,
            platformOptions,
        };
    };

    const handleCreate = async (actionType, secondaryAction = null) => {
        setApiWait(true);
        const basePayload = buildPayload();
        if (!basePayload) {
            setApiWait(false);
            return;
        }

        let payload;

        // --- START OF FIX: Simplified and corrected logic ---
        // Rely solely on `actionType` passed from the button click.
        switch (actionType) {
            case 'draft':
                payload = { ...basePayload, status: 'draft', scheduledTime: scheduleDates };
                break;
            case 'publish_now':
                payload = { ...basePayload, status: 'publish_now' };
                break;
            case 'schedule':
            default:
                payload = { ...basePayload, status: 'scheduled', scheduledTime: scheduleDates };
                break;
        }
        // --- END OF FIX ---

        try {
            await postApi(payload);
            const successMessage = {
                draft: 'saved as draft',
                publish_now: 'published',
                scheduled: 'scheduled'
            }[payload.status];
            
            message.success(`Post successfully ${successMessage}.`);

            if (secondaryAction === 'new') {
                resetForm(false);
            } else if (secondaryAction === 'duplicate') {
                resetForm(true);
            } else {
                navigate("/Dashboard/QueuePage");
            }
        } catch (error) {
            if (error.response?.data?.errorType === 'LIMIT_REACHED') {
                showUpgradeModal({ message: error.response.data.message, featureName: 'Posts' });
            } else {
                message.error(error.message || "An unexpected error occurred.");
            }
        } finally {
            setApiWait(false);
        }
    };
    
    const handleUpdate = async (action) => {
        setApiWait(true);
        const basePayload = buildPayload();
        if (!basePayload) {
            setApiWait(false);
            return;
        }
    
        let finalPayload = { ...basePayload };
    
        // --- START OF FIX: Corrected 'publishNow' logic ---
        if (action === 'saveDraft') {
            finalPayload.status = 'draft';
            finalPayload.scheduledTime = scheduleDates;
        } else if (action === 'schedule') {
            finalPayload.status = 'scheduled';
            finalPayload.scheduledTime = scheduleDates;
        } else if (action === 'publishNow') {
            // Correctly set status to 'publish_now' and remove schedule time
            finalPayload.status = 'publish_now';
            finalPayload.scheduledTime = null; 
        }
        // --- END OF FIX ---
    
        try {
            await updateScheduledPost(PublishData._id, finalPayload);
            message.success("Post updated successfully!");
            navigate("/Dashboard/QueuePage");
        } catch (error) {
            if (error.isLimitError) {
                showUpgradeModal({ message: error.message, featureName: 'Posts' });
            } else {
                message.error(error.message || "Failed to update post.");
            }
        } finally {
            setApiWait(false);
        }
    };

    const publishMenuItems = [
        { key: 'new', label: 'Publish and New', onClick: () => handleCreate('publish_now', 'new') },
        { key: 'duplicate', label: 'Publish and Duplicate', onClick: () => handleCreate('publish_now', 'duplicate') },
    ];

    const scheduleMenuItems = [
        { key: 'new', label: 'Schedule and New', onClick: () => handleCreate('schedule', 'new') },
        { key: 'duplicate', label: 'Schedule and Duplicate', onClick: () => handleCreate('schedule', 'duplicate') },
    ];
    
    const handleAiImagesGenerated = (imageUrls) => {
        const newItems = imageUrls.map(url => ({
            id: `ai_${Date.now()}_${Math.random()}`,
            localUrl: url,
            permanentUrl: url,
            type: 'image',
            file: null,
            status: 'completed',
        }));
        setPhotoPreview(prev => [...prev, ...newItems]);
    };

    const handleSocialPostGenerated = (itemsToInsert) => {
        if (!Array.isArray(itemsToInsert) || itemsToInsert.length === 0) return;
    
        const newMediaItems = itemsToInsert.flatMap(item => 
            (item.imageUrls || []).map(url => ({
                id: `inserted_${Date.now()}_${Math.random()}`,
                localUrl: url,
                permanentUrl: url,
                type: 'image', 
                file: null,
                status: 'completed'
            }))
        );
    
        setPhotoPreview(prev => [...prev, ...newMediaItems]);
    
        const allCaptions = itemsToInsert
            .map(p => p.caption)
            .filter(Boolean) 
            .join('\n\n---\n\n');
    
        if (allCaptions) {
            setMessage(prev => prev ? `${prev}\n\n${allCaptions}` : allCaptions);
        }
    };

    const isTikTokSelected = selectedProfiles.some(p => p.platform === 'TIKTOK');
    
    const isTiktokValid = useMemo(() => {
        if (!isTikTokSelected) return true;
        
        const opts = tiktokOptions || {};
        const commercialToggleOn = opts.brand_content_toggle === true;
        const commercialTypeSelected = !!opts.branded_content_type;

        if (!opts.privacy_level) return false;
        if (commercialToggleOn && !commercialTypeSelected) return false;

        return true;
    }, [isTikTokSelected, tiktokOptions]);

    const renderFooter = () => {
        const isDraftDisabled = allValidationErrors.length > 0 || !hasContent;
        const isScheduleDisabled = isDraftDisabled || selectedProfiles.length === 0 || !isTiktokValid;

        if (isEditing) {
            const mainButtonText = scheduleType === 'now' ? 'Publish Now' : (PublishData?.status === 'failed' ? 'Retry Schedule' : 'Update Schedule');
            const mainButtonAction = scheduleType === 'now' ? 'publishNow' : 'schedule';

            return (
                <>
                    {(PublishData?.status === 'draft' || PublishData?.status === 'failed') &&
                        <Button onClick={() => handleUpdate('saveDraft')} loading={apiWait} size="large" disabled={isDraftDisabled}>
                            Save Draft
                        </Button>
                    }
                    <Button
                        type="primary"
                        size="large"
                        loading={apiWait}
                        onClick={() => handleUpdate(mainButtonAction)}
                        disabled={isScheduleDisabled}
                    >
                        {mainButtonText}
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <Button onClick={() => handleCreate('draft')} loading={apiWait} size="large" disabled={isDraftDisabled}>Save as Draft</Button>
                    <Dropdown.Button
                        type="primary"
                        size="large"
                        loading={apiWait}
                        onClick={() => handleCreate(scheduleType === 'now' ? 'publish_now' : 'schedule')}
                        menu={{ items: scheduleType === 'now' ? publishMenuItems : scheduleMenuItems }}
                        disabled={isScheduleDisabled}
                    >
                        {scheduleType === 'now' ? 'Publish' : 'Schedule'}
                    </Dropdown.Button>
                </>
            );
        }
    };

    return (
        <div className="flex flex-col h-screen bg-gray-100">
            <header className="flex items-center justify-between p-4 border-b bg-white z-10">
                <h1 className="text-xl font-bold text-gray-800">{isEditing ? 'Edit Post' : 'Create a New Post'}</h1>
                <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800">
                    <i className="fa-solid fa-times text-xl"></i>
                </button>
            </header>

            <main className="flex-grow grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 overflow-y-auto">
                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 space-y-5 flex flex-col">
                    {isEditing && PublishData?.status === 'failed' && (
                        <Alert
                            message="This post failed to publish"
                            description={PublishData.failureReason || "Please review the post content and selected profiles, then try scheduling again."}
                            type="error"
                            showIcon
                            closable
                        />
                    )}
                    <ProfileSelector
                        availableProfiles={availableProfiles}
                        selectedProfiles={selectedProfiles}
                        setSelectedProfiles={setSelectedProfiles} 
                        isProfileSelected={isProfileSelected}
                        onProfileSelectionChange={handleProfileSelectionChange}
                        showProfileList={showProfileList}
                        setShowProfileList={setShowProfileList}
                        validationErrors={{}}
                    />
                    <div className="flex-grow">
                        <PostUploader
                            onFileUploadError={handleFileUploadError}
                            messages={messages} setMessage={setMessage} photoPreview={photoPreview}
                            setPhotoPreview={setPhotoPreview} instagramOptions={instagramOptions}
                            setInstagramOptions={setInstagramOptions} pinterestOptions={pinterestOptions}
                            setPinterestOptions={setPinterestOptions} 
                            linkedinOptions={linkedinOptions}
                            setLinkedinOptions={setLinkedinOptions}
                            youtubeOptions={youtubeOptions}
                            setYoutubeOptions={setYoutubeOptions}
                            tiktokOptions={tiktokOptions}
                            setTiktokOptions={setTiktokOptions}
                            scheduleDates={scheduleDates}
                            setScheduleDates={setScheduleDates} availableProfiles={availableProfiles}
                            selectedProfiles={selectedProfiles} scheduleType={scheduleType}
                            setScheduleType={setScheduleType}
                            brandTimezone={brandTimezone}
                            setIsCaptionModalOpen={setIsCaptionModalOpen}
                            setIsImageModalOpen={setIsImageModalOpen}
                            setIsSocialPostModalOpen={setIsSocialPostModalOpen}
                        />
                    </div>
                    <ValidationTab
                        photoPreview={photoPreview}
                        messages={messages}
                        selectedProfiles={selectedProfiles}
                        pinterestOptions={pinterestOptions}
                        linkedinOptions={linkedinOptions}
                        youtubeOptions={youtubeOptions}
                        scheduleType={scheduleType}
                        scheduleDates={scheduleDates}
                        onValidationChange={handlePostValidationChange}
                        fileErrors={fileValidationErrors}
                    />
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200 space-y-4">
                    <h2 className="text-lg font-semibold text-gray-700">Network Previews</h2>
                    <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-2">
                        {selectedProfiles.length > 0 ? selectedProfiles.map((profile, index) => (
                            <div key={`${profile.platform}-${profile.platform_pageId || profile.platform_id}-${index}`}>
                                {profile.platform === 'FACEBOOK' && <FacebookModal Previewimages={photoPreview} Previewcaptions={messages} profile={profile} />}
                                {profile.platform === 'INSTAGRAM' && <InstagarmModal Previewimages={photoPreview} Previewcaptions={messages} profile={profile} instagramOptions={instagramOptions} />}
                                {profile.platform === 'PINTEREST' && (
                                    <Pinerest
                                        Previewimages={photoPreview}
                                        pinTitle={pinterestOptions[profile.platform_id]?.title || ''}
                                        pinDescription={messages}
                                        profile={profile}
                                        thumbnailUrl={pinterestOptions[profile.platform_id]?.thumbnailUrl || null}
                                    />
                                )}
                                {profile.platform === 'LINKEDIN' && (
                                    <LinkedInModal 
                                        mediaItems={photoPreview} 
                                        captions={messages} 
                                        profile={profile} 
                                        linkedinOptions={linkedinOptions} 
                                    />
                                )}
                                {profile.platform === 'YOUTUBE' && (
                                    <YoutubeModal
                                        Previewimages={photoPreview}
                                        Previewcaptions={messages}
                                        profile={profile}
                                        title={youtubeOptions[profile.platform_id]?.title}
                                    />
                                )}
                                {profile.platform === 'TIKTOK' && (
                                    <TikTokModal
                                        Previewimages={photoPreview}
                                        Previewcaptions={messages}
                                        profile={profile}
                                    />
                                )}
                            </div>
                        )) : (
                            <div className="text-center text-gray-500 py-20">Select a profile to see a preview.</div>
                        )}
                    </div>
                </div>
            </main>

            <footer className="flex items-center justify-end p-4 border-t bg-white gap-3 z-10">
                {isTikTokSelected && (
                    <p className="text-xs text-center text-gray-500 mr-auto">
                        By posting, you agree to TikTok's Music Usage Confirmation.
                    </p>
                )}
                {renderFooter()}
            </footer>
            
            <UpgradeModal isOpen={upgradeModalVisible} onClose={hideUpgradeModal} message={upgradeModalMessage} featureName={upgradeModalFeature} />

            <CaptionGenerator isOpen={isCaptionModalOpen} onClose={() => setIsCaptionModalOpen(false)} setMessage={setMessage} mediaItems={photoPreview} />
            <AiImageGenerator isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} onImagesGenerated={handleAiImagesGenerated} />
            <SocialPostGeneratorModal
                isOpen={isSocialPostModalOpen}
                onClose={() => setIsSocialPostModalOpen(false)}
                onPostGenerated={handleSocialPostGenerated}
            />
        </div>
    );
}

const ProfileSelector = ({ availableProfiles, selectedProfiles, setSelectedProfiles, isProfileSelected, onProfileSelectionChange, showProfileList, setShowProfileList, validationErrors }) => (
    <div className="relative">
        <button
            className="flex w-full items-center justify-between p-3 border rounded-lg bg-gray-50 hover:border-blue-400"
            onClick={() => setShowProfileList(!showProfileList)}
        >
            <div className="flex items-center gap-2 flex-wrap">
                {selectedProfiles.length > 0 ? (
                    <>
                        {selectedProfiles.slice(0, 2).map((p, index) => (
                            <span key={`${p.platform}-${p.platform_pageId || p.platform_id}-${index}`} className="flex items-center gap-1.5 bg-white border px-2 py-1 rounded-full text-sm">
                                {renderPlatformIcon(p.platform)}
                                {renderProfileName(p)}
                            </span>
                        ))}
                        {selectedProfiles.length > 2 && (
                            <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full">
                                +{selectedProfiles.length - 2} more
                            </span>
                        )}
                    </>
                ) : (
                    <span className="text-gray-500">Select profiles to post to...</span>
                )}
            </div>
            <i className={`fa ${showProfileList ? 'fa-angle-up' : 'fa-angle-down'} text-gray-500`}></i>
        </button>

        {showProfileList && (
            <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg">
                <div className="p-2 flex justify-end gap-2 border-b">
                    <Button size="small" onClick={() => setSelectedProfiles(availableProfiles)}>Select All</Button>
                    <Button size="small" onClick={() => setSelectedProfiles([])}>Clear All</Button>
                </div>
                <ul className="p-2 max-h-60 overflow-y-auto">
                    <ShowProfiles
                        avalibaleProfile={availableProfiles}
                        CheckPlatform={isProfileSelected}
                        ChangePlatform={onProfileSelectionChange}
                        validationErrors={validationErrors} 
                    />
                </ul>
            </div>
        )}
    </div>
);